import { groupBucketRows } from "./buckets";

export type MaturityRow = {
	/** Full, human-readable instrument name. */
	instrumentName: string;
	tickerName: string;
	/** ISO date string */
	expirationDate: string | null;
	/** € repaid at maturity; null/absent = N/A. Only meaningful in a quantity portfolio. */
	maturedValue?: number | null;
};

export type MaturityGroup = {
	/** Stable bucket id (the API bucket id). */
	key: string;
	/** Full label with the unit spelled out, e.g. "0-1 years" or "10+ years". */
	groupName: string;
	/** Compact label for the bucket bar, e.g. "0-1Y" or "10Y+". */
	shortName: string;
	/** Portfolio weight of the bucket, as a percentage (0-100). */
	weight: number;
	/** Sum of the rows' present matured values; null when every row is N/A. */
	total?: number | null;
	rows: MaturityRow[];
};

export type CouponRow = {
	/** Full, human-readable instrument name. */
	instrumentName: string;
	tickerName: string;
	/** ISO date string */
	couponDate: string | null;
	/** Coupon rate as a denormalized percentage (0-100). */
	couponRate: number | null;
	/** € paid on the coupon date; null/absent = N/A. Only meaningful in a quantity portfolio. */
	couponAmount?: number | null;
};

export type CouponGroup = {
	/** Stable bucket id (the API bucket id). */
	key: string;
	/** Full label with the unit spelled out, e.g. "0-3 months" or "6-12 months". */
	groupName: string;
	/** Compact label for the bucket bar, e.g. "0-3M" or "6-12M". */
	shortName: string;
	/** Portfolio weight of the bucket, as a percentage (0-100). */
	weight: number;
	/** Sum of the rows' present coupon amounts; null when every row is N/A. */
	total?: number | null;
	rows: CouponRow[];
};

export type MaturityCouponTrackerData = {
	maturityData: MaturityGroup[];
	couponData: CouponGroup[];
};

// --- Dedicated Maturity & Coupon tracker API shape (normalized from the generated types) -------

/** A time-period bucket with its portfolio weight. Shared by the maturity and coupon sections. */
export type MaturityCouponBucket = {
	id: string;
	/** Portfolio weight of the bucket, as a percentage (0-100). */
	weight: number;
};

/** A bond in the maturity section, bucketed by time-to-maturity. */
export type MaturityCouponBond = {
	name: string;
	/** ISO date string */
	expirationDate: string | null;
	/** € repaid at maturity. */
	maturedValue?: number | null;
	bucketId: string;
};

/** A coupon in the coupon section, bucketed by time-to-next-coupon. */
export type MaturityCouponCoupon = {
	name: string;
	/** ISO date string */
	couponDate: string | null;
	/** Coupon rate as a fraction (0.025 = 2.5%), as returned by the API. */
	couponRate: number | null;
	/** € paid on the coupon date. */
	couponAmount?: number | null;
	bucketId: string;
};

export type MaturityCouponTrackerResponse = {
	buckets: MaturityCouponBucket[];
	bonds: MaturityCouponBond[];
	coupons: MaturityCouponCoupon[];
};

/** Groups the tracker response's bonds into their maturity buckets. */
export function toMaturityGroups(response: MaturityCouponTrackerResponse): MaturityGroup[] {
	return groupBucketRows(
		response.buckets,
		response.bonds,
		(bond) => ({
			instrumentName: bond.name,
			tickerName: bond.name,
			expirationDate: bond.expirationDate,
			maturedValue: bond.maturedValue,
		}),
		(row) => row.maturedValue,
	);
}

/** Groups the tracker response's coupons into their coupon buckets. */
export function toCouponGroups(response: MaturityCouponTrackerResponse): CouponGroup[] {
	return groupBucketRows(
		response.buckets,
		response.coupons,
		(coupon) => ({
			instrumentName: coupon.name,
			tickerName: coupon.name,
			couponDate: coupon.couponDate,
			// The API returns the rate as a fraction; the tracker displays a percentage (0-100).
			couponRate: coupon.couponRate == null ? null : coupon.couponRate * 100,
			couponAmount: coupon.couponAmount,
		}),
		(row) => row.couponAmount,
	);
}
